import ReportService from './reportService';
import {
  mapAppointmentTypes,
  mapPatientDistribution,
  mapPatientFollowup,
  mapPatientGrowthTrends,
  mapPatientRetention,
  mapPatientVisitFrequency,
  mapVisitRepetitionReturn,
  mapBillsReport,
  buildSummaryCards,
} from './reportMappers';
import { dateUtils } from '../utils/reportParams';

const SUCCESS = 'success';
const FAILURE = 'failure';

function ensureDateRange(params = {}) {
  const { startDate, endDate } = params;
  if (startDate && endDate) return params;
  const defaults = dateUtils.getDefaultDateRange();
  return {
    ...params,
    startDate: startDate || defaults.startDate,
    endDate: endDate || defaults.endDate,
  };
}

function wrapResult(name, promise, tag) {
  return promise
    .then((data) => {
      tag(`${name}.${SUCCESS}`, { ok: true });
      return { name, ok: true, data };
    })
    .catch((error) => {
      tag(`${name}.${FAILURE}`, { ok: false, message: error?.message || String(error) });
      return { name, ok: false, error };
    });
}

const ReportLoader = {
  async loadAndMap(rawParams = {}) {
    const logs = [];
    const tag = (name, snapshot) => logs.push({ name, snapshot });

    const params = ensureDateRange(rawParams);
    tag('params', params);

    // Dynamically build request plan only for methods that exist on ReportService.
    // This prevents missing endpoints (e.g. bills) from throwing TypeErrors and
    // keeps each request independent so one failing or missing API doesn't stop others.
    const desiredEndpoints = {
      appointmentTypes: 'getAppointmentTypes',
      patientGrowth: 'getPatientGrowthTrends',
      visitRepetition: 'getVisitRepetitionReturn',
      patientRetention: 'getPatientRetention',
      patientFollowup: 'getPatientFollowup',
      patientDistribution: 'getPatientDistribution',
      // bills: 'getBillsReport', // optional - may be missing on some environments
      patientVisitFrequency: 'getPatientVisitFrequency',
      activeUsers: 'getActiveUsers',
      newUsers: 'getNewUsers',
    };

    const requestPlan = {};
    Object.entries(desiredEndpoints).forEach(([key, methodName]) => {
      if (typeof ReportService[methodName] === 'function') {
        requestPlan[key] = () => ReportService[methodName](params);
      } else {
        // tag missing endpoints so logs show which APIs were skipped
        tag(`skipped.${key}`, { reason: 'method_missing', methodName });
        // ensure raw slot exists as null so downstream mapping sees empty input
        raw[key] = null;
      }
    });

    const raw = {};
    const mapped = {};
    const errors = {};

    try {
      const results = await Promise.all(
        Object.entries(requestPlan).map(([name, fn]) => wrapResult(name, fn(), tag)),
      );

      results.forEach((result) => {
        if (result.ok) {
          raw[result.name] = result.data;
          tag(`raw.${result.name}`, result.data);
        } else {
          raw[result.name] = null;
          errors[result.name] = result.error;
        }
      });

      const safeMap = (label, mapper, source, fallback) => {
        try {
          const value = mapper(source);
          tag(`mapped.${label}`, value);
          return value;
        } catch (error) {
          errors[label] = error;
          tag(`mapped.${label}.error`, error?.message || String(error));
          return fallback;
        }
      };

      mapped.visitTrendData = safeMap('visitTrendData', mapAppointmentTypes, raw.appointmentTypes, []);
      mapped.patientGrowthData = safeMap('patientGrowthData', mapPatientGrowthTrends, raw.patientGrowth, []);

      const visitRepetitionMapped = safeMap('visitRepetition', mapVisitRepetitionReturn, raw.visitRepetition, {
        visitRepetitionData: [],
        returnRate: null,
      });
      mapped.visitRepetitionData = visitRepetitionMapped.visitRepetitionData || [];
      mapped.visitRepetitionReturnRate = visitRepetitionMapped.returnRate ?? null;

      mapped.patientRetention = safeMap('patientRetention', mapPatientRetention, raw.patientRetention, {
        retentionByPeriod: [],
        overallRetention: [],
      });

      const followUpMapped = safeMap('followUp', mapPatientFollowup, raw.patientFollowup, {
        compliance: [],
        byPeriod: [],
        chartData: [],
      });
      mapped.followUpCompliance = followUpMapped.compliance;
      mapped.followUpByPeriod = followUpMapped.byPeriod;
      mapped.followUpChart = followUpMapped.chartData;

      const distributionMapped = safeMap('patientDistribution', mapPatientDistribution, raw.patientDistribution, {
        ageData: [],
        genderData: [],
        totals: { age: 0, gender: 0 },
      });
      mapped.ageData = distributionMapped.ageData;
      mapped.genderData = distributionMapped.genderData;

      mapped.patientVisitFrequency = safeMap('patientVisitFrequency', mapPatientVisitFrequency, raw.patientVisitFrequency, []);

  // Bills report (revenue + refunds)
  const billsMapped = safeMap('bills', mapBillsReport, raw.bills, {
    revenue: { total: 0, byDoctor: [], byPeriod: [] },
    refunds: { total: 0, incomplete: 0, missed: 0, byDoctor: [], byPeriod: [] },
  });
      mapped.bills = billsMapped;

      // ensure active/new users are available to the summary card builder
      mapped.activeUsers = raw.activeUsers || [];
      mapped.newUsers = raw.newUsers || [];

      mapped.summaryCards = buildSummaryCards({
        appointmentTypes: mapped.visitTrendData,
        patientRetention: mapped.patientRetention,
        followUp: followUpMapped,
        patientGrowth: mapped.patientGrowthData,
        visitRepetition: visitRepetitionMapped,
        activeUsers: mapped.activeUsers,
        newUsers: mapped.newUsers,
        bills: mapped.bills,
      });

      if (typeof console !== 'undefined' && console.groupCollapsed) {
        console.groupCollapsed('[ReportLoader] Loaded reports - summary');
        logs.forEach((entry) => console.log(entry.name, entry.snapshot));
        console.groupEnd();
      }

      return { raw, mapped, logs, errors: Object.keys(errors).length ? errors : null };
    } catch (error) {
      console.error('[ReportLoader] loadAndMap error', error);
      return { raw, mapped, logs, errors: { loader: error } };
    }
  },
};

export default ReportLoader;
